<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue"
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router"
import { Tab } from "bootstrap"
import axiosIns from "@/plugins/axios.js"
import Banner from '@/views/frontend/pages/trabaja_en_agv/dashboard_postulante/components/banner.vue'
import SideBar from '@/views/frontend/pages/trabaja_en_agv/dashboard_postulante/components/sidebar.vue'
import DatosPersonales from '@/views/frontend/pages/trabaja_en_agv/dashboard_postulante/datos_personales.vue'
import EducacionExpLab from '@/views/frontend/pages/trabaja_en_agv/dashboard_postulante/educacion_experiencia_lab.vue'
import Footer from '@/views/frontend/pages/trabaja_en_agv/dashboard_postulante/components/footer.vue'
import { useToastr } from "@/composables/useToastr"
import { confirmDialog } from "@/composables/useConfirm"

const toastr = useToastr();
const id_applicant = JSON.parse(localStorage.getItem('userData'))?.id_usuario
const data = ref({
    postulante: {
        name:"",
        first_surname:"",
        second_surname:"",
        type_doc:"",
        n_doc:"",
        n_phone:"",
        birthdate: null,       
        gender:"",
        
        marital_status:"",
        nationality:"",
        country:"",
        departament:"",
        province:"",
        district:"",
        pers_description:"",
        salary_expect:"",
    },
    educacion: [],
    experiencia: [],
    idiomas: []
})

const data_before_edit = ref()
const isDisabledSave = ref(true)

const changes = reactive({
    postulante: {},      // Datos personales
    educacion: [],       // Educación
    experiencia: [],     // Experiencia
    idiomas: [],         // Idiomas
})

const handleSaveDatosPersonales = (payload) => {
    // console.log("CAMBIOS DETECTADOS EN DATOS PERSOANLES")
    changes.postulante = { ...changes.postulante, ...payload }
}

// Cada instancia de EducacionExpLab (region="educacion"/"experiencia") solo
// envía las claves que le corresponden; las demás quedan sin tocar.
const handleSaveEduExp = (payload) => {
    if ('educacion' in payload) changes.educacion = payload.educacion
    if ('experiencia' in payload) changes.experiencia = payload.experiencia
    if ('idiomas' in payload) changes.idiomas = payload.idiomas
}

// Hay cambios sin guardar mientras alguno de estos siga con contenido. Se limpian
// recién cuando el guardado se completa con éxito (ver saveData).
const hasUnsavedChanges = computed(() => {
    return Object.keys(changes.postulante).length > 0
        || changes.educacion.length > 0
        || changes.experiencia.length > 0
        || changes.idiomas.length > 0
})

const resetChanges = () => {
    changes.postulante = {}
    changes.educacion = []
    changes.experiencia = []
    changes.idiomas = []
}

// Aviso al cerrar/recargar la pestaña si hay datos sin guardar
const beforeUnloadHandler = (e) => {
    if (hasUnsavedChanges.value) {
        e.preventDefault()
        e.returnValue = ''
    }
}

// Aviso al navegar a otra vista dentro de la app (menú, "Puestos de Trabajo", etc.)
onBeforeRouteLeave(() => {
    if (hasUnsavedChanges.value) {
        return confirmDialog({
            title: "Datos sin guardar",
            message: 'Tienes datos sin guardar. Si sales de esta vista se perderán hasta que des clic en "Actualizar datos". ¿Deseas salir igualmente?',
            confirmText: "Salir igualmente",
            cancelText: "Quedarme",
        })
    }
    return true
})

const eduRef = ref(null)
const expRef = ref(null)

const getData = () => {
    const params = {
        id : id_applicant,
    }

    const config = {
        method: "post",
        url: "/talentoagv/getApplicant/",
        headers: {
            'Content-Type': 'application/json'
        },
        data: params
    };

    axiosIns(config)
    .then(function (response) {
        if (response.data["is_error"] === false) {
            
            const parseDate = (val) => {
                if (!val) return null
                const d = new Date(val)
                if (isNaN(d.getTime()) || d.getTime() === 0) return null
                return d
            }
            const parseBool = (val) => val === true || val === 'true' || val === 'True'

            response.data["result"]["postulante"]["birthdate"] = parseDate(response.data["result"]["postulante"]["birthdate"])

            if(response.data["result"]["educacion"]){
                for(let ed of response.data['result']['educacion']){
                    ed['start_date'] = parseDate(ed.start_date)
                    ed['end_date'] = parseDate(ed.end_date)
                    ed['present'] = parseBool(ed.present)
                }
            }

            if(response.data["result"]["experiencia"]){
                for(let ex of response.data['result']['experiencia']){
                    ex['start_date'] = parseDate(ex.start_date)
                    ex['end_date'] = parseDate(ex.end_date)
                    ex['present'] = parseBool(ex.present)
                }
            }
            
            data.value = response.data["result"];
            data_before_edit.value = JSON.parse(JSON.stringify(response.data["result"]));

        }
    })
    .catch(function (error) {
        console.log(error);
    });
}

const saveData = async () => {

    const accepted = await confirmDialog({
        title: "Actualizar datos",
        message: "¿Deseas actualizar tus datos personales?",
        confirmText: "Sí, actualizar",
        cancelText: "Cancelar",
    })
    if (!accepted) return

    if (eduRef.value) {
        const eduData = eduRef.value.getDataForSave()
        changes.educacion = eduData.educacion
        changes.idiomas = eduData.idiomas
    }
    if (expRef.value) {
        const expData = expRef.value.getDataForSave()
        changes.experiencia = expData.experiencia
    }

    let payload = {}
    payload = { ...changes }

    if(Object.keys(payload.postulante || {}).length === 0) delete payload.postulante
    // Una lista vacía solo se omite si ya estaba vacía antes de editar; si el usuario
    // eliminó todos los registros, se envía vacía para que se den de baja.
    const hadRecords = (key) => (data_before_edit.value?.[key] || []).length > 0
    if(!payload.educacion || (payload.educacion.length === 0 && !hadRecords('educacion'))) delete payload.educacion
    if(!payload.experiencia || (payload.experiencia.length === 0 && !hadRecords('experiencia'))) delete payload.experiencia
    if(!payload.idiomas || (payload.idiomas.length === 0 && !hadRecords('idiomas'))) delete payload.idiomas

    if (Object.keys(payload).length === 0) {
        toastr.warning("No se encontraron cambios.", "Advertencia");
        return
    }

    const data = {
        id : id_applicant,
        payload : payload,
        data_before_edit : data_before_edit.value,
    }

    const config = {
        method: "post",
        url: "/talentoagv/updApplicant/",
        data: data
    }

    axiosIns(config)
    .then(function (response) {
        if (response.data && response.data.errors && response.data.errors.length > 0) {
            toastr.warning(response.data.errors.join(', '), "Advertencia");
            getData()
        } else {
            toastr.success("Datos actualizados correctamente.✍🏻", "Operación exitosa.👍🏻");
            resetChanges()
            // Recargo la página para que el usuario vea reflejado el cambio de inmediato
            setTimeout(() => window.location.reload(), 1200)
        }
    })
    .catch(function (error) {
        console.log(error);
        toastr.error("Error al actualizar los datos. Intente nuevamente.", "Error");
    })

}

// --- Pestaña con link propio (?tab=perfil|experiencia|educacion|acceso) ---
const route = useRoute()
const router = useRouter()

const TAB_IDS = {
    perfil: 'solid-rounded-justified-tab1',
    experiencia: 'solid-rounded-justified-tab2',
    educacion: 'solid-rounded-justified-tab4',
}

const activateTabFromQuery = () => {
    const targetId = TAB_IDS[route.query.tab]
    if (!targetId) return
    const trigger = document.querySelector(`a[href="#${targetId}"]`)
    if (trigger) new Tab(trigger).show()
}

// Al hacer clic, además de que Bootstrap cambia la pestaña visualmente,
// actualizamos la URL para que quede como link directo a esa pestaña.
const goToTab = (key) => {
    router.replace({ query: { ...route.query, tab: key } })
}

// Reacciona también si ya estás en esta página y cambias el ?tab= (ej. desde el
// menú del avatar), sin necesidad de recargar.
watch(() => route.query.tab, () => activateTabFromQuery())

onMounted(() => {
    getData()
    window.addEventListener('beforeunload', beforeUnloadHandler)
    nextTick(() => activateTabFromQuery())
})

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', beforeUnloadHandler)
})

</script>
<template>
    <Banner/>
    <section class="section">
        <div class="container-fluid profile-container mt-5">
            <div class="row mb-4">
                <div class="col-12">
                    <h6 class="text-end" style="text-decoration: underline; text-underline-offset: 6px; color: #1957d1;">
                        <router-link class="text-agv-siete" to="/dashboard_ofertas_laborales">
                            <i class="fa-solid fa-briefcase me-1"></i>
                            &nbsp;Puestos de Trabajo
                        </router-link>
                    </h6>
                </div>
            </div>

            <div class="row profile-layout g-2">
                <!-- Columna izquierda: perfil resumido (persistente en todas las pestañas) -->
                <div class="col-lg-2">
                    <SideBar :data="data['postulante']"/>
                </div>

                <!-- Columna central: pestañas -->
                <div class="col-lg-8">
                    <div class="tabs tabs-wrapper mt-2">
                        <ul class="nav nav-tabs nav-tabs-solid nav-tabs-rounded nav-justified">
                            <li class="nav-item">
                                <a
                                class="nav-link p-3 active"
                                href="#solid-rounded-justified-tab1"
                                data-bs-toggle="tab"
                                @click="goToTab('perfil')"
                                >Perfil&nbsp;
                                <i class="fa-solid fa-address-card"></i>
                            </a>
                            </li>
                            <li class="nav-item">
                                <a
                                class="nav-link p-3"
                                href="#solid-rounded-justified-tab2"
                                data-bs-toggle="tab"
                                @click="goToTab('experiencia')"
                                >Experiencia&nbsp;
                                <i class="fa-solid fa-briefcase"></i>
                                </a>
                            </li>
                            <li class="nav-item">
                                <a
                                class="nav-link p-3"
                                href="#solid-rounded-justified-tab4"
                                data-bs-toggle="tab"
                                @click="goToTab('educacion')"
                                >Educación&nbsp;
                                <i class="fa-solid fa-user-graduate"></i>
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div class="tab-content mt-1">
                        <div class="tab-pane show active" id="solid-rounded-justified-tab1">
                            <div class="tab-save-bar">
                                <button class="btn-agv-cuatro b-radius px-3 py-2" style="font-size: 14px;" @click="saveData">
                                    <i class="fa-solid fa-save"></i>&nbsp;Actualizar datos
                                </button>
                            </div>
                            <DatosPersonales
                            :data="data['postulante']"
                            @save-data="handleSaveDatosPersonales"
                            />
                        </div>
                        <div class="tab-pane" id="solid-rounded-justified-tab2">
                            <div class="tab-save-bar">
                                <button class="btn-agv-cuatro b-radius px-3 py-2" style="font-size: 14px;" @click="saveData">
                                    <i class="fa-solid fa-save"></i>&nbsp;Actualizar datos
                                </button>
                            </div>
                            <EducacionExpLab
                            ref="expRef"
                            region="experiencia"
                            :data="data"
                            @save-edu-exp="handleSaveEduExp"
                            />
                        </div>
                        <div class="tab-pane" id="solid-rounded-justified-tab4">
                            <div class="tab-save-bar">
                                <button class="btn-agv-cuatro b-radius px-3 py-2" style="font-size: 14px;" @click="saveData">
                                    <i class="fa-solid fa-save"></i>&nbsp;Actualizar datos
                                </button>
                            </div>
                            <EducacionExpLab
                            ref="eduRef"
                            region="educacion"
                            :data="data"
                            @save-edu-exp="handleSaveEduExp"
                            />
                        </div>
                    </div>
                </div>

                <!-- Columna derecha: estado del CV (persistente en todas las pestañas) -->
                <div class="col-lg-2 mt-2">
                    <EducacionExpLab
                    region="cv"
                    :data="data"
                    @cv-uploaded="getData"
                    />
                </div>
            </div>
        </div>
    </section>
    <Footer/>
</template>

<style scoped>
/* Espacio a los costados de la barra de pestañas (Perfil/Experiencia/Educación) */
.tabs-wrapper {
    padding: 0 20px;
}

.tab-save-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 14px;
}

.profile-container {
    max-width: 1700px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 32px;
    padding-right: 32px;
}

.profile-layout {
    align-items: flex-start;
}
</style>